<template>
  <div class="pc-register-layout">
    <section class="register-intro">
      <p class="section-eyebrow">Invited Registration</p>
      <h1>创建点指之间账号</h1>
      <p class="intro-copy">
        在电脑端完成手机号验证后，即可加入点指之间数字生态。注册成功后可直接查看 APP 下载渠道，也可以继续返回官网了解 VitaeAI。
      </p>
      <div class="intro-points">
        <div>
          <strong>01</strong>
          <span>邀请码选填，支持自动填入</span>
        </div>
        <div>
          <strong>02</strong>
          <span>手机号验证码注册，账号可同时用于官网登录</span>
        </div>
        <div>
          <strong>03</strong>
          <span>注册不强制下载，下载不强制注册</span>
        </div>
      </div>
    </section>

    <form class="register-panel" novalidate @submit.prevent="submit">
      <header>
        <div>
          <span>Account</span>
          <h2>注册新账号</h2>
        </div>
        <img src="/logos/vitaeai-mark.svg" alt="" />
      </header>

      <label class="invite-field">
        <span>邀请码</span>
        <input
          v-model.trim="inviteCode"
          maxlength="32"
          autocomplete="off"
          placeholder="输入好友邀请码（选填）"
          @blur="lookupInviter"
        />
      </label>

      <div class="inviter-card" :class="{ loading: inviterLoading }" aria-live="polite">
        <template v-if="inviter">
          <img v-if="inviter.avatar" :src="inviter.avatar" alt="" />
          <i v-else>{{ inviterName.slice(0, 1) }}</i>
          <span>
            <strong>{{ inviterName }}</strong>
            <small>邀请你一起加入点指之间</small>
          </span>
        </template>
        <template v-else>
          <i>Inv</i>
          <span>
            <strong>{{ inviterLoading ? '正在查询邀请码...' : '邀请信息' }}</strong>
            <small>{{ inviterLoading ? '请稍候' : '填写邀请码后自动确认邀请关系' }}</small>
          </span>
        </template>
      </div>

      <div class="register-grid">
        <label>
          <span>手机号</span>
          <input v-model.trim="form.phone" inputmode="tel" maxlength="11" autocomplete="tel" placeholder="请输入 11 位手机号" />
        </label>
        <label>
          <span>验证码</span>
          <span class="code-field">
            <input v-model.trim="form.code" inputmode="numeric" maxlength="6" autocomplete="one-time-code" placeholder="6 位验证码" />
            <button type="button" :disabled="countdown > 0 || sendingCode" @click="sendVerification">
              {{ sendingCode ? '发送中' : countdown ? `${countdown}s` : '获取验证码' }}
            </button>
          </span>
        </label>
        <label>
          <span>设置密码</span>
          <input v-model="form.password" type="password" minlength="6" maxlength="18" autocomplete="new-password" placeholder="6-18 位密码" />
        </label>
        <label>
          <span>确认密码</span>
          <input v-model="form.confirmPassword" type="password" minlength="6" maxlength="18" autocomplete="new-password" placeholder="再次输入密码" />
        </label>
      </div>

      <label class="agreement-field">
        <input v-model="agreed" type="checkbox"/>
        <div class="agreement-copy">
          我已阅读并同意
          <button type="button" @click="openAgreement('USER_AGREEMENT')">《用户协议》</button>
          和
          <button type="button" @click="openAgreement('PRIVATE_POLICY')">《隐私政策》</button>
        </div>
      </label>

      <p v-if="message" class="register-message" :class="{ error: hasError }" role="alert">{{ message }}</p>

      <div class="register-actions">
        <button class="primary-cta" type="submit" :disabled="submitting">
          {{ submitting ? '正在注册...' : '注册并查看下载' }}
        </button>
        <button class="secondary-cta" type="button" @click="emit('navigate', '/download')">直接下载 APP</button>
      </div>
    </form>
  </div>

  <Teleport to="body">
    <div v-if="agreementVisible" class="pc-agreement-mask" @click.self="closeAgreement">
      <section class="pc-agreement-modal" role="dialog" aria-modal="true" aria-labelledby="pc-agreement-title">
        <header>
          <div>
            <span>Legal</span>
            <h2 id="pc-agreement-title">{{ agreement?.title || agreementTitle }}</h2>
          </div>
          <button type="button" aria-label="关闭协议" @click="closeAgreement">×</button>
        </header>
        <div v-if="agreementLoading" class="agreement-state">正在加载协议...</div>
        <div v-else-if="agreementError" class="agreement-state error">
          <p>协议加载失败，请稍后重试。</p>
          <button class="secondary-cta" type="button" @click="loadAgreement">重新加载</button>
        </div>
        <article v-else v-html="agreement?.content || '暂无协议内容'"></article>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { getAgreementContent, getInviterInfo, registerAccount, sendRegistrationCode } from '@/api/auth'

const emit = defineEmits(['navigate', 'registered'])

const form = reactive({ phone: '', code: '', password: '', confirmPassword: '' })
const inviteCode = ref('')
const source = ref('platform_internal_share')
const inviter = ref(null)
const inviterLoading = ref(false)
const lookedCode = ref('')
const agreed = ref(false)
const submitting = ref(false)
const sendingCode = ref(false)
const countdown = ref(0)
const message = ref('')
const hasError = ref(false)
const agreementVisible = ref(false)
const agreementType = ref('USER_AGREEMENT')
const agreement = ref(null)
const agreementLoading = ref(false)
const agreementError = ref(false)
const agreementCache = new Map()
let countdownTimer = null

const inviterName = computed(() => inviter.value?.name || inviter.value?.nickname || '点指之间用户')
const agreementTitle = computed(() => (agreementType.value === 'PRIVATE_POLICY' ? '隐私政策' : '用户协议'))

function collectParams() {
  const params = new URLSearchParams(window.location.search)
  const hashQuery = window.location.hash.split('?')[1]
  if (hashQuery) {
    new URLSearchParams(hashQuery).forEach((value, key) => {
      if (!params.has(key)) params.set(key, value)
    })
  }
  return params
}

function firstParam(params, keys) {
  for (const key of keys) {
    const value = params.get(key)?.trim()
    if (value) return value
  }
  return ''
}

function setMessage(text, error = false) {
  message.value = text
  hasError.value = error
}

async function lookupInviter() {
  const code = inviteCode.value.trim()
  if (!code) {
    inviter.value = null
    lookedCode.value = ''
    return
  }
  if (code === lookedCode.value && inviter.value) return

  lookedCode.value = code
  inviter.value = null
  inviterLoading.value = true
  try {
    const result = await getInviterInfo(code)
    inviter.value = result && Object.keys(result).length ? result : null
    if (!inviter.value) setMessage('暂未查询到该邀请码，请确认后重试', true)
  } catch (error) {
    inviter.value = null
    setMessage(error.message || '邀请码查询失败，请稍后重试', true)
  } finally {
    inviterLoading.value = false
  }
}

function validPhone() {
  return /^1[3-9]\d{9}$/.test(form.phone)
}

async function sendVerification() {
  if (!validPhone()) {
    setMessage('请输入正确的手机号', true)
    return
  }
  if (countdown.value || sendingCode.value) return

  sendingCode.value = true
  try {
    await sendRegistrationCode(form.phone)
    countdown.value = 60
    countdownTimer = window.setInterval(() => {
      countdown.value -= 1
      if (!countdown.value) window.clearInterval(countdownTimer)
    }, 1000)
    setMessage('验证码已发送，请查收短信')
  } catch (error) {
    setMessage(error.message || '验证码发送失败', true)
  } finally {
    sendingCode.value = false
  }
}

async function submit() {
  if (!validPhone()) return setMessage('请输入正确的手机号', true)
  if (!/^\d{6}$/.test(form.code)) return setMessage('请输入 6 位验证码', true)
  if (form.password.length < 6 || form.password.length > 18) return setMessage('密码长度需为 6-18 位', true)
  if (form.password !== form.confirmPassword) return setMessage('两次密码不一致', true)
  if (!agreed.value) return setMessage('请先阅读并同意协议', true)

  submitting.value = true
  try {
    await registerAccount({
      phone: form.phone,
      code: form.code,
      password: form.password,
      inviteCode: inviteCode.value || undefined,
      registerSource: source.value
    })
    sessionStorage.setItem('dzzj_pc_register_success', JSON.stringify({
      inviteCode: inviteCode.value,
      source: source.value,
      phone: form.phone,
      inviter: inviter.value
    }))
    emit('registered')
  } catch (error) {
    setMessage(error.message || '注册失败，请稍后重试', true)
  } finally {
    submitting.value = false
  }
}

async function loadAgreement() {
  if (agreementCache.has(agreementType.value)) {
    agreement.value = agreementCache.get(agreementType.value)
    return
  }
  agreementLoading.value = true
  agreementError.value = false
  try {
    const result = await getAgreementContent(agreementType.value)
    agreement.value = result
    agreementCache.set(agreementType.value, result)
  } catch {
    agreementError.value = true
  } finally {
    agreementLoading.value = false
  }
}

function openAgreement(type) {
  agreementType.value = type
  agreement.value = agreementCache.get(type) || null
  agreementVisible.value = true
  loadAgreement()
}

function closeAgreement() {
  agreementVisible.value = false
}

onMounted(() => {
  const params = collectParams()
  inviteCode.value = firstParam(params, ['inviteCode', 'invite_code', 'info'])
  source.value = (firstParam(params, ['channel', 'campaign', 'utm_source', 'utm_campaign']) || 'platform_internal_share').slice(0, 64)
  if (inviteCode.value) lookupInviter()
})

onBeforeUnmount(() => {
  if (countdownTimer) window.clearInterval(countdownTimer)
})
</script>

<style scoped>
.pc-register-layout {
  display: grid;
  max-width: 1240px;
  grid-template-columns: minmax(0, 0.9fr) minmax(420px, 560px);
  align-items: start;
  gap: 58px;
  margin: 0 auto;
}

.register-intro h1 {
  margin: 18px 0;
  color: var(--ink);
  font-family: var(--display-font);
  font-size: clamp(44px, 5vw, 68px);
  font-weight: 400;
  line-height: 1.05;
}

.intro-copy {
  max-width: 520px;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.9;
}

.intro-points {
  display: grid;
  gap: 12px;
  margin-top: 38px;
}

.intro-points div {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  padding: 17px 18px;
  border: 1px solid rgba(222, 246, 255, 0.12);
  border-radius: 14px;
  background: rgba(4, 10, 21, 0.64);
}

.intro-points strong {
  color: var(--cyan);
  font-family: var(--latin-font);
  font-size: 20px;
}

.intro-points span {
  color: var(--muted);
  font-size: 14px;
  line-height: 1.6;
}

.register-panel {
  padding: clamp(26px, 3vw, 38px);
  border: 1px solid rgba(222, 246, 255, 0.15);
  border-radius: 24px;
  background: linear-gradient(150deg, rgba(98, 244, 214, 0.08), transparent 42%), rgba(4, 10, 21, 0.86);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.36);
  backdrop-filter: blur(18px);
}

.register-panel header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 26px;
}

.register-panel header span {
  color: var(--cyan);
  font-family: var(--latin-font);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.register-panel header h2 {
  margin-top: 7px;
  color: var(--ink);
  font-size: 28px;
}

.register-panel header img {
  width: 42px;
  height: 42px;
}

.register-panel label {
  display: grid;
  gap: 8px;
}

.register-panel label > span {
  color: var(--muted);
  font-size: 13px;
  font-weight: 750;
}

.register-panel input {
  width: 100%;
  min-height: 48px;
  padding: 0 15px;
  border: 1px solid rgba(222, 246, 255, 0.16);
  border-radius: 10px;
  color: var(--ink);
  background: rgba(4, 10, 21, 0.72);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.register-panel input:focus {
  border-color: rgba(98, 244, 214, 0.58);
  box-shadow: 0 0 0 4px rgba(98, 244, 214, 0.09);
}

.invite-field {
  margin-bottom: 13px;
}

.inviter-card {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 22px;
  padding: 13px 15px;
  border: 1px solid rgba(222, 246, 255, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.035);
}

.inviter-card.loading {
  opacity: 0.72;
}

.inviter-card img,
.inviter-card i {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 50%;
  object-fit: cover;
}

.inviter-card i {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #04110f;
  font-size: 15px;
  font-style: normal;
  font-weight: 900;
  background: radial-gradient(circle at 30% 25%, #8df5de, #46d6b6);
}

.inviter-card span {
  min-width: 0;
}

.inviter-card strong {
  display: block;
  color: var(--ink);
  font-size: 14px;
}

.inviter-card small {
  display: block;
  margin-top: 3px;
  color: var(--quiet);
  font-size: 12px;
}

.register-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.code-field {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 112px;
  gap: 8px;
}

.code-field button {
  min-height: 48px;
  border: 1px solid rgba(98, 244, 214, 0.34);
  border-radius: 10px;
  color: var(--cyan);
  font-size: 12.5px;
  font-weight: 850;
  background: rgba(98, 244, 214, 0.08);
}

.code-field button:disabled {
  color: var(--quiet);
  cursor: not-allowed;
  background: rgba(222, 246, 255, 0.035);
}

.register-panel .agreement-field {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 22px 0 18px;
}

.agreement-field input {
  width: 17px;
  min-height: 17px;
  height: 17px;
  margin: 3px 0 0;
  accent-color: #46d6b6;
}

.agreement-copy {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: nowrap;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.7;
  white-space: nowrap;
}

.agreement-field button {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  color: var(--cyan);
  font-size: inherit;
  font-weight: 800;
  line-height: inherit;
  background: transparent;
  white-space: nowrap;
}

.register-message {
  margin-bottom: 18px;
  color: var(--cyan);
  font-size: 13px;
  line-height: 1.7;
}

.register-message.error {
  color: #ff9c9c;
}

.register-actions {
  display: flex;
  gap: 12px;
}

.register-actions .primary-cta,
.register-actions .secondary-cta {
  flex: 1;
}

.pc-agreement-mask {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(1, 4, 10, 0.76);
  backdrop-filter: blur(8px);
}

.pc-agreement-modal {
  display: grid;
  width: min(880px, 100%);
  max-height: min(78vh, 760px);
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 1px solid rgba(222, 246, 255, 0.18);
  border-radius: 22px;
  background: rgba(4, 10, 21, 0.98);
  box-shadow: 0 34px 110px rgba(0, 0, 0, 0.5);
}

.pc-agreement-modal header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px;
  border-bottom: 1px solid rgba(222, 246, 255, 0.12);
}

.pc-agreement-modal header span {
  color: var(--cyan);
  font-family: var(--latin-font);
  font-size: 10.5px;
  font-weight: 850;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.pc-agreement-modal h2 {
  margin-top: 6px;
  color: var(--ink);
  font-size: 24px;
}

.pc-agreement-modal header button {
  width: 38px;
  height: 38px;
  border: 1px solid rgba(222, 246, 255, 0.16);
  border-radius: 12px;
  color: var(--muted);
  font-size: 24px;
  background: rgba(255, 255, 255, 0.04);
}

.pc-agreement-modal article,
.agreement-state {
  overflow-y: auto;
  padding: 26px 30px 34px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.9;
}

.agreement-state {
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 18px;
  text-align: center;
}

.agreement-state.error {
  color: #ff9c9c;
}

@media (max-width: 1080px) {
  .pc-register-layout {
    grid-template-columns: 1fr;
    gap: 34px;
  }
}

@media (max-width: 640px) {
  .register-grid,
  .register-actions {
    grid-template-columns: 1fr;
    flex-direction: column;
  }
}
</style>
